import generatePicker from "antd/es/date-picker/generatePicker";
import dayjs, { Dayjs } from "dayjs";
import dayjsGenerateConfig from "rc-picker/lib/generate/dayjs";
import { LegacyRef, useEffect, useRef, useState } from "react";
import { ReactComponent as ArrowRightRegularIcon } from "../../../assets/icons/font-awesome/arrow-right-regular.svg";
import { DatePickerRef, RangeValue } from "./types";

const DatePicker = generatePicker<Dayjs>(dayjsGenerateConfig);

const { RangePicker } = DatePicker;

export function CustomDatePicker({
  onDatesSelected,
  dates,
  setDates,
  noOfDays,
  enableFuture,
  className,
  disablePast,
}: {
  dates?: RangeValue<dayjs.Dayjs>;
  setDates?: (dates?: RangeValue<dayjs.Dayjs>) => void;
  onDatesSelected: (startDate: string, endDate: string) => void;
  noOfDays: number;
  enableFuture?: boolean;
  disablePast?: boolean;
  className?: string;
}) {
  const datePickerRef = useRef<DatePickerRef>();
  const [value, setValue] = useState<RangeValue<dayjs.Dayjs> | undefined>(
    dates
  );
  const [open, setOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!dates) {
      setValue(undefined);
    }
  }, [dates]);

  const onCalendarChange = (val: RangeValue<dayjs.Dayjs>) => {
    setDates && setDates(val);
    if (val?.[0] && val[1]) {
      onDatesSelected(
        val[0].startOf("day").toISOString(),
        val[1].endOf("day").toISOString()
      );
      datePickerRef && datePickerRef.current?.blur();
    }
  };

  const onOpenChange = (open: boolean) => {
    if (open) {
      setOpen(true);
      setDates && setDates([] as unknown as RangeValue<dayjs.Dayjs>);
    } else {
      setOpen(false);
    }
  };

  const disabledDate = (current: dayjs.Dayjs) => {
    const tooLate =
      dates && dates[0] && current.diff(dates[0], "day") >= noOfDays;
    const tooEarly =
      dates && dates[1] && dates[1].diff(current, "day") >= noOfDays;
    const isFuture = !enableFuture && current > dayjs().endOf("day");
    const isPast = disablePast && current < dayjs().startOf("day");

    return tooEarly || tooLate || isFuture || isPast ? true : false;
  };

  return (
    <>
      <div
        className={`dr-max-w-224 dr-cursor-pointer dr-h-36 dr-min-h-36 dr-bg-darwin-touch-neutral-0 BodyBody2Regular dr-rounded-8 dr-border ${
          value
            ? "dr-border-darwin-touch-primary-primary"
            : "dr-border-darwin-touch-neutral-200"
        } payment-date-picker hover:dr-bg-darwin-touch-neutral-50`}
      >
        <RangePicker
          style={{
            paddingLeft: ".5rem",
            paddingRight: ".5rem",
            paddingTop: ".125rem",
            paddingBottom: ".125rem",
            margin: 0,
            height: "100%",
            outline: "none",
            border: 0,
            fontSize: "0.8125rem",
            fontWeight: 400,
            lineHeight: "1rem",
            background: "transparent",
          }}
          separator={
            <ArrowRightRegularIcon
              className={`${value ? "" : "dr-text-darwin-touch-neutral-600"}`}
            />
          }
          ref={datePickerRef as LegacyRef<DatePickerRef>}
          value={open ? ([] as unknown as RangeValue<Dayjs>) : value}
          disabledDate={disabledDate}
          size="small"
          inputReadOnly={true}
          popupStyle={{ fontFamily: "Lato" }}
          className={className}
          onCalendarChange={onCalendarChange}
          onChange={dates => {
            setValue(dates);
          }}
          onOpenChange={onOpenChange}
          allowClear={false}
          format={date => dayjs(date).format("D MMM YYYY")}
          suffixIcon={false}
        />
      </div>
      <style>{`
        .payment-date-picker
          > .ant-picker-range
          > .ant-picker-input
          input::placeholder {
          font-size: 0.8125rem !important;
          font-weight: 400 !important;
          line-height: 1rem !important;
          color: #767676 !important;
        }
        .payment-date-picker > .ant-picker-range > .ant-picker-input > input {
          font-size: 0.8125rem !important;
          font-weight: 400 !important;
          line-height: 1rem !important;
          color: #1a1a1a !important;
        }
        .payment-date-picker
          > .ant-picker-range
          > .ant-picker-input-active
          > input {
          font-size: 0.8125rem !important;
          font-weight: 400 !important;
          line-height: 1rem !important;
          color: #1a1a1a !important;
        }
      `}</style>
    </>
  );
}
